import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, Star, Plane, Briefcase, Gem, Sparkles } from "lucide-react";

const iconMap = { airport: Plane, corporate: Briefcase, vip: Gem, events: Sparkles };

export default function Home() {
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/packages").then(r => setPackages(r.data)).catch(() => {});
    api.get("/testimonials").then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/hero.jpg"
            alt="Luxury car"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="grain" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-20">
          <div className="max-w-3xl fade-up">
            <p className="eyebrow mb-6">— Caribbean Luxury · Since 2024</p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.02] mb-6">
              Luxury in
              <span className="block italic gradient-gold">Motion.</span>
            </h1>
            <p className="text-lg md:text-xl text-[#b8c5d1] max-w-xl leading-relaxed mb-10">
              Unparalleled transportation for discerning clients who appreciate luxury and style — with the warmth, flair, and vibrance of the Caribbean.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-book-btn" className="btn-gold">
                Book Your Ride <ArrowRight size={16}/>
              </Link>
              <Link to="/fleet" data-testid="hero-fleet-btn" className="btn-ghost">View Fleet</Link>
            </div>

            <div className="mt-16 flex items-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="flex">{[...Array(5)].map((_,i)=><Star key={i} size={14} className="fill-[#5DCFE5] text-[#5DCFE5]"/>)}</div>
                <span className="text-[#6b7c8a]">5-Star Service</span>
              </div>
              <div className="text-[#6b7c8a]"><span className="text-[#5DCFE5] font-bold">24/7</span> Availability</div>
              <div className="text-[#6b7c8a]"><span className="text-[#5DCFE5] font-bold">Miami</span> · Fort Lauderdale · Palm Beach</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section bg-[#050a12]" id="services">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <p className="eyebrow mb-3">Services</p>
              <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-2xl">
                Every journey, <span className="italic gradient-gold">curated.</span>
              </h2>
              <div className="divider"/>
            </div>
            <p className="text-[#6b7c8a] max-w-md text-sm leading-relaxed">
              From airport pickups to red-carpet celebrations — choose your experience and let us deliver excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {packages.map(p => {
              const Icon = iconMap[p.id] || Gem;
              return (
                <Link key={p.id} to={`/book/${p.id}`} data-testid={`service-card-${p.id}`} className="card-dark overflow-hidden group">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"/>
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon size={18} className="text-[#5DCFE5]"/>
                      <p className="eyebrow">{p.tagline}</p>
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl mb-3">{p.name}</h3>
                    <p className="text-sm text-[#6b7c8a] mb-6 leading-relaxed">{p.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-[#1a2838]">
                      <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#4a5868]">Fare</div>
                        {p.pricing_model === "hourly" ? (
                          <div className="font-serif text-2xl text-[#5DCFE5]">${p.hourly_rate}<span className="text-sm text-[#b8c5d1]"> / hour</span></div>
                        ) : (
                          <div className="font-serif text-2xl text-[#5DCFE5]">$20<span className="text-base">+</span><span className="text-sm text-[#b8c5d1]"> $3/mi</span></div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm tracking-[0.18em] uppercase text-[#5DCFE5]">
                        Book <ArrowRight size={14}/>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section bg-[#030710] relative overflow-hidden">
        <div className="grain"/>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center relative">
          <div className="aspect-[4/5] overflow-hidden rounded-lg bg-[#0c1825] flex items-center justify-center">
            <img src="/images/onianto.jpg" alt="Onianto - Founder" className="w-full h-full object-contain"/>
          </div>
          <div>
            <p className="eyebrow mb-3">About</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight mb-3">
              My name is <span className="italic gradient-gold">Onianto.</span>
            </h2>
            <div className="divider"/>
            <p className="text-[#b8c5d1] leading-relaxed mb-5">
              Founded by a proud Jamaican entrepreneur, our elite transportation service was built on a simple vision: to combine world-class professionalism with the warmth, flair, and vibrance of the Caribbean.
            </p>
            <p className="text-[#6b7c8a] leading-relaxed mb-5">
              From airport transfers to corporate travel and special celebrations, we ensure our guests feel both pampered and welcomed — as if every journey were a taste of the islands.
            </p>
            <p className="text-[#6b7c8a] leading-relaxed mb-8 italic">
              "At the heart of our brand is a promise: professionalism with personality."
            </p>
            <Link to="/about" data-testid="about-learn-more" className="btn-ghost">Our Story <ArrowRight size={14}/></Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="section bg-[#050a12]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3">What Our Clients Say</p>
            <h2 className="font-serif text-4xl md:text-6xl">Real <span className="italic gradient-gold">stories.</span></h2>
            <div className="divider mx-auto"/>
          </div>

          {testimonials.length === 0 ? (
            <div className="max-w-4xl mx-auto grid md:grid-cols-[auto,1fr] gap-12 items-center">
              <div className="w-48 h-64 md:w-56 md:h-72 rounded-lg overflow-hidden mx-auto">
                <img src="/images/testimonial.png" alt="Andrew R" className="w-full h-full object-cover"/>
              </div>
              <div>
                <div className="flex mb-6">
                  {[...Array(5)].map((_,i)=><Star key={i} size={18} className="fill-[#5DCFE5] text-[#5DCFE5]"/>)}
                </div>
                <blockquote className="font-serif text-2xl md:text-3xl leading-tight mb-6 italic">
                  "The most comfortable and convenient way to travel in Miami."
                </blockquote>
                <p className="text-[#5DCFE5] tracking-[0.2em] uppercase text-sm">— Andrew R</p>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="card-dark p-7">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_,j) => (
                      <Star key={j} size={14} className={j < t.rating ? "fill-[#5DCFE5] text-[#5DCFE5]" : "text-[#1a2838]"}/>
                    ))}
                  </div>
                  <blockquote className="font-serif text-lg leading-snug mb-4 italic text-[#f0f6fb]">
                    "{t.comment}"
                  </blockquote>
                  <p className="text-[#5DCFE5] tracking-[0.2em] uppercase text-xs">— {t.customer_name.split(" ")[0]} {t.customer_name.split(" ")[1]?.[0] || ""}{t.customer_name.split(" ")[1] ? "." : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-br from-[#0c1825] via-[#050a12] to-[#0c1825] relative">
        <div className="grain"/>
        <div className="max-w-5xl mx-auto text-center relative">
          <h2 className="font-serif text-4xl md:text-6xl leading-tight mb-6">
            Ready to ride in <span className="italic gradient-gold">style?</span>
          </h2>
          <p className="text-[#b8c5d1] max-w-xl mx-auto mb-10">
            Book your luxury experience in under two minutes. Add champagne, decorations, and red carpet for an unforgettable arrival.
          </p>
          <Link to="/book" data-testid="cta-book" className="btn-gold">Reserve Now <ArrowRight size={16}/></Link>
        </div>
      </section>

      <Footer/>
    </div>
  );
}
